// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { ChartContainer, Table } from '@shared/ui-kit/components';
import { type ValueCreationHolding } from '@shared/data-access/interfaces/common/aggregator';
import { useConst } from '@shared/ui-kit';
import { createColumnHelper } from '@tanstack/react-table';
import { type FC, type ReactNode, useMemo } from 'react';
import { ValueCreationProgress } from './value-creation-progress';
import { useTranslation } from 'react-i18next';

export interface ValueCreationProps {
  readonly data: ValueCreationHolding[];
  readonly name: string | ReactNode;
  readonly 'data-testid'?: string;
  readonly currency: string;
  readonly uppercaseNameColumn?: boolean;
}

export const ValueCreation: FC<ValueCreationProps> = ({
  name,
  data,
  'data-testid': testId,
  currency,
  uppercaseNameColumn = false,
}) => {
  const { t } = useTranslation();
  const columnHelper = useConst(createColumnHelper<ValueCreationHolding>());

  const max = useMemo(
    () => Math.max(...data.map(item => Math.abs(item.valueCreated))),
    [data],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        cell: ({ getValue }) =>
          uppercaseNameColumn ? (
            <span className="uppercase">{getValue()}</span>
          ) : (
            <span>{getValue()}</span>
          ),
      }),
      columnHelper.accessor('weight', {
        header: t('common.weight', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data format="percentage" value={getValue()} />
        ),
      }),
      columnHelper.accessor('initialValue', {
        header: t('glossary.initial-value', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data format="currency" value={getValue()} currency={currency} />
        ),
      }),
      columnHelper.accessor('finalValue', {
        header: t('glossary.final-value', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data format="currency" value={getValue()} currency={currency} />
        ),
      }),
      columnHelper.accessor('valueCreated', {
        header: t('glossary.value-created', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data
            format="currency"
            value={getValue()}
            validate
            currency={currency}
          />
        ),
      }),
      columnHelper.accessor('valueCreated', {
        id: 'valueCreatedBar',
        header: '',
        enableSorting: false,
        cell: ({ getValue }) => (
          <ValueCreationProgress value={getValue()} max={max} min={-max} />
        ),
      }),
      columnHelper.accessor('return', {
        header: t('glossary.return', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data format="percentage" value={getValue()} validate />
        ),
      }),
    ],
    [columnHelper, currency, max, t, uppercaseNameColumn],
  );

  return (
    <ChartContainer
      header={`${t('glossary.value-creation', { ns: 'glossary' })}: ${name}`}
      className="[&_.chart-container]:min-h-[auto]"
      data-testid={testId}
    >
      <div className="overflow-x-auto">
        <Table.Grouped
          columns={columns}
          data={data}
          className="rem:max-h-[320px]"
          initialSorting={[{ id: 'valueCreated', desc: true }]}
          data-testid="value-creation-table"
        />
      </div>
    </ChartContainer>
  );
};
